import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../i18n';
import * as qbase from '../services/authClient';   // 모드(local|qbase)에 따라 로컬 세션 또는 QBase 세션
import { getMaintenance } from '../services/maintenance';

/** 큐베이스 점검 때문에 실패했나 (qbaseClient 의 QbaseError.maintenance) — 로그아웃으로 다루지 않는다 */
const isMaintenanceFailure = (e: unknown): boolean => (e as { maintenance?: boolean } | null)?.maintenance === true;
const maintenanceWaitMs = (): number => {
  const sec = Number(getMaintenance()?.retryAfterSec);
  return (sec > 0 ? sec : 30) * 1000;
};

export interface AuthUser { loginId: string; usrid: string; name: string; email: string; viewOnly: boolean }

export interface AuthState {
  status: 'idle' | 'checking' | 'authenticated' | 'anonymous';
  user: AuthUser | null;
  homeSysId: string | null;
  activeSysId: string | null;
  error: string | null;
  /** 열어 둔 채로 세션이 끊겼다 — 화면은 그대로 두고 다시 로그인 창을 띄운다 (services/sessionExpiry) */
  expired: boolean;
  /** 다시 로그인 진행 중 — status 는 건드리지 않는다(앱이 통째로 사라지지 않게) */
  reauthBusy: boolean;
}

const initialState: AuthState = { status: 'idle', user: null, homeSysId: null, activeSysId: null, error: null, expired: false, reauthBusy: false };

const toUser = (u: qbase.QbaseSession['user']): AuthUser => ({
  loginId: u.loginId, usrid: u.usrid, name: u.name, email: u.email,
  viewOnly: u.viewOnly === true || u.viewOnly === 'Y',
});

/** 마운트/새로고침 시 — 세션 확인 → 작업 시스템 확정. 로그인 경로가 늘어도 이 한 지점만 거친다. */
export const restoreSession = createAsyncThunk('auth/restore', async () => {
  // 큐베이스 DB 전환 재기동 틈(services/maintenance)이면 '세션 없음'으로 보지 않고 기다렸다 다시 묻는다 —
  // 로그인 화면으로 떨어뜨리지 않는다. 띠가 '전환 중'을 알린다. 너무 길어지면(약 10분) 원래 오류로 끝낸다.
  let session: Awaited<ReturnType<typeof qbase.getSession>> = null;
  for (let tries = 0; ; tries++) {
    try { session = await qbase.getSession(); break; } catch (e) {
      if (!isMaintenanceFailure(e) || tries >= 20) throw e;
      await new Promise((r) => setTimeout(r, maintenanceWaitMs()));
    }
  }
  if (!session) return null;
  let activeSysId: string | null;
  try {
    activeSysId = await qbase.ensureActiveSystem(session);
  } catch (e) {
    if (!isMaintenanceFailure(e)) throw e;
    // 점검 중(쓰기 동결)이라 작업 시스템 선택을 서버 세션에 저장하지 못했다. 이 경우에만 서버 세션 값 →
    // 계정 소속 순으로 쓴다(ensureActiveSystem 이 고를 값과 같다). 점검이 끝난 뒤 새로고침하면 서버에도 확정된다.
    activeSysId = session.activeSysId ?? session.homeSysId ?? null;
  }
  const user = toUser(session.user);
  if (user.viewOnly) {
    // 새로고침으로 세션이 바뀌어도 편집권을 되찾는다 — 점검 중엔 못 가져오므로 조회 전용 그대로 둔다
    try { user.viewOnly = !(await qbase.acquireEdit()); } catch (e) { if (!isMaintenanceFailure(e)) throw e; }
  }
  return { user, homeSysId: session.homeSysId, activeSysId };
});

/** 조회 전용 상태에서 사용자가 명시적으로 편집권을 가져온다 (다른 세션이 놓았을 때) */
export const acquireEdit = createAsyncThunk('auth/acquireEdit', async () => qbase.acquireEdit());

export const signIn = createAsyncThunk('auth/signIn', async (cred: { loginId: string; pwd: string }, { rejectWithValue }) => {
  try {
    const { viewOnly } = await qbase.login(cred.loginId, cred.pwd);
    const session = await qbase.getSession();
    if (!session) throw new Error(i18n.t('layout:auth.sessionAfterLogin'));
    const activeSysId = await qbase.ensureActiveSystem(session);
    return { user: { ...toUser(session.user), viewOnly }, homeSysId: session.homeSysId, activeSysId };
  } catch (e: any) {
    return rejectWithValue(e?.message || i18n.t('layout:auth.loginFailed'));
  }
});

/** 체험 계정으로 바로 로그인 — 로그인 화면의 계정 리본 (비밀번호 없이, 서버가 대신 로그인) */
export const signInTrial = createAsyncThunk('auth/signInTrial', async (loginId: string, { rejectWithValue }) => {
  try {
    const { viewOnly } = await qbase.trialLogin(loginId);
    const session = await qbase.getSession();
    if (!session) throw new Error(i18n.t('layout:auth.sessionAfterLogin'));
    const activeSysId = await qbase.ensureActiveSystem(session);
    return { user: { ...toUser(session.user), viewOnly }, homeSysId: session.homeSysId, activeSysId };
  } catch (e: any) {
    return rejectWithValue(e?.message || i18n.t('layout:auth.loginFailed'));
  }
});

/**
 * 세션이 끊긴 뒤 **화면을 유지한 채** 다시 로그인한다 — signIn 과 달리 status 를 건드리지 않는다.
 * signIn 은 pending 에서 'checking', 실패하면 'anonymous' 가 되는데, 그러면 앱이 로그인 화면으로 바뀌고
 * 메모리의 캔버스(저장하지 않은 편집)가 사라진다. 비밀번호를 한 번 잘못 친 것으로 그러면 안 된다.
 */
export const reauthenticate = createAsyncThunk('auth/reauth', async (cred: { loginId: string; pwd: string }, { rejectWithValue }) => {
  try {
    const { viewOnly } = await qbase.login(cred.loginId, cred.pwd);
    const session = await qbase.getSession();
    if (!session) throw new Error(i18n.t('layout:auth.sessionAfterLogin'));
    const activeSysId = await qbase.ensureActiveSystem(session);
    return { user: { ...toUser(session.user), viewOnly }, homeSysId: session.homeSysId, activeSysId };
  } catch (e: any) {
    return rejectWithValue(e?.message || i18n.t('layout:auth.loginFailed'));
  }
});

export const signOut = createAsyncThunk('auth/signOut', async () => { await qbase.logout(); });

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthError: (state) => { state.error = null; },
    /** 401 을 만났다 — 로그인 상태였을 때만 (로그인 화면에서의 401 은 정상 흐름이다) */
    sessionExpired: (state) => { if (state.status === 'authenticated') state.expired = true; },
  },
  extraReducers: (b) => {
    b.addCase(restoreSession.pending, (s) => { s.status = 'checking'; s.error = null; });
    b.addCase(restoreSession.fulfilled, (s, a) => {
      s.expired = false;
      if (!a.payload) { s.status = 'anonymous'; s.user = null; s.homeSysId = null; s.activeSysId = null; return; }
      s.status = 'authenticated'; s.user = a.payload.user; s.homeSysId = a.payload.homeSysId; s.activeSysId = a.payload.activeSysId;
    });
    b.addCase(restoreSession.rejected, (s, a) => { s.status = 'anonymous'; s.error = a.error.message || i18n.t('layout:auth.sessionFailed'); });

    b.addCase(signIn.pending, (s) => { s.status = 'checking'; s.error = null; });
    b.addCase(signIn.fulfilled, (s, a) => {
      s.expired = false;
      s.status = 'authenticated'; s.user = a.payload.user; s.homeSysId = a.payload.homeSysId; s.activeSysId = a.payload.activeSysId;
    });
    b.addCase(signIn.rejected, (s, a: PayloadAction<any>) => { s.status = 'anonymous'; s.error = (a.payload as string) || i18n.t('layout:auth.loginFailed'); });
    b.addCase(signInTrial.pending, (s) => { s.status = 'checking'; s.error = null; });
    b.addCase(signInTrial.fulfilled, (s, a) => {
      s.expired = false;
      s.status = 'authenticated'; s.user = a.payload.user; s.homeSysId = a.payload.homeSysId; s.activeSysId = a.payload.activeSysId;
    });
    b.addCase(signInTrial.rejected, (s, a: PayloadAction<any>) => { s.status = 'anonymous'; s.error = (a.payload as string) || i18n.t('layout:auth.loginFailed'); });

    b.addCase(reauthenticate.pending, (s) => { s.reauthBusy = true; s.error = null; });
    b.addCase(reauthenticate.fulfilled, (s, a) => {
      s.reauthBusy = false; s.expired = false; s.status = 'authenticated';
      s.user = a.payload.user; s.homeSysId = a.payload.homeSysId; s.activeSysId = a.payload.activeSysId;
    });
    b.addCase(reauthenticate.rejected, (s, a: PayloadAction<any>) => { s.reauthBusy = false; s.error = (a.payload as string) || i18n.t('layout:auth.loginFailed'); });
    b.addCase(signOut.fulfilled, () => ({ ...initialState, status: 'anonymous' }));
    b.addCase(acquireEdit.fulfilled, (s, a) => { if (s.user) s.user.viewOnly = !a.payload; });
  },
});

export const { clearAuthError, sessionExpired } = authSlice.actions;
export default authSlice.reducer;
